import { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import AppVersion from "@Components/AppVersion";
import Footer from "@Components/footer/Footer";
import Header from "@Components/header/Header";
import classes from "@Components/header/Header.module.scss";
import MetaHead from "@Components/meta-head/";
import YotpoSwellCustomerIdentification from "@Components/yotpo-product-review/YotpoSwellCustomerIdentification";
import { DY_PAGE_CONTEXT } from "@Constants/common";
import { titleTail } from "@Constants/meta";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import {
  setActiveCookieConsent,
  setDYContext,
} from "@Libs/dynamic-yield/dynamicYield";
import { dyIdentify } from "@Libs/dynamic-yield/dynamicYield";
import { getItem } from "@Utils/browserStorage";
import { isCanadianLocale } from "@Utils/locale";
import { useCurrentPageContextByPath } from "@wff/hooks/useCurrentPageContextByPath";
import { useMembershipForCA } from "@wff/hooks/useMembershipForCA";
import { CustomerInfoSelector } from "@wff/store/customerInfoSlice";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";

interface AppLayoutProps {
  pageTitle?: string;
  productSKU?: string;
  pageDescription?: string;
  headTitleTail?: string; // pass empty string to remove the titleTail from the <title> tag
  mainClass?: string;
  children?: React.ReactNode;
  pageModel?: any;
  showHeader?: boolean;
}

export function AppLayout(props: AppLayoutProps) {
  const {
    children,
    pageTitle = "",
    productSKU,
    pageDescription = "",
    headTitleTail = titleTail,
    mainClass = "layout__main",
    pageModel,
    showHeader = true,
  } = props;
  const [isShowOverlay, setShowOverlay] = useState();
  const currentLocale = useAppSelector(localeSelector);
  const isCanadianUser = isCanadianLocale(currentLocale);
  const { showTopNavMenusItemBecomeMemberForCA, showUnlockFreeDelBtnForCA } =
    useMembershipForCA();
  const currentPageContext = useCurrentPageContextByPath();
  const customerInfo = useAppSelector(CustomerInfoSelector);
  const customerEmail = customerInfo.email;

  let modifiedHeaderParams = { ...pageModel?.siteGlobal?.header };

  if (isCanadianUser) {
    modifiedHeaderParams = {
      ...pageModel?.siteGlobal?.header,
      showTopNavMenusItemBecomeMemberForCA,
      showUnlockFreeDelBtnForCA,
      pageTitle,
      productSKU,
    };
  }

  /**
   * ToDo: From Nuwan
   * Extend this useEffect to address the all DY Page Contexts
   * For Now, this useEffect will consider only the `OTHER` DY Page Context.
   * This will prevent of declaring page level DY 'OTHER' Page Context.
   */
  //Set 'OTHER' DY-PageContext
  useEffect(() => {
    const dyCookieConsentStatus =
      getItem(STORAGE_KEYS.DY_COOKIE_CONSENT, STORAGE_TYPES.LOCAL) || "false";
    const consent = dyCookieConsentStatus === "true" ? true : false;
    setActiveCookieConsent(consent);

    if (currentPageContext === DY_PAGE_CONTEXT.OTHER) {
      setDYContext(DY_PAGE_CONTEXT.OTHER, currentLocale?.languageCode);
    }
  }, [currentLocale?.languageCode, currentPageContext]);

  useEffect(() => {
    if (customerEmail) {
      dyIdentify(customerEmail);
    }
  }, [customerEmail]);

  const isCookieModelOpen = (value: any) => {
    setShowOverlay(value);
  };

  const isMobile = useMediaQuery("(max-width:48em)");
  const isShowOverlayout = isMobile && isShowOverlay === true;

  return (
    <>
      <MetaHead
        headTitle={pageModel?.pageTitle ?? pageTitle}
        headDescription={pageModel?.pageDescription ?? pageDescription}
        headTitleTail={headTitleTail}
      />
      <div className={classes[isShowOverlayout ? "app-layout_overlay" : ""]}>
        {showHeader && pageModel?.siteGlobal?.header && (
          <Header {...modifiedHeaderParams} />
        )}

        <YotpoSwellCustomerIdentification customerInfo={customerInfo} />
        <main className={mainClass}>{children}</main>

        {pageModel?.siteGlobal?.footer && (
          <Footer
            {...pageModel?.siteGlobal?.footer}
            isCookieModelOpen={isCookieModelOpen}
          />
        )}

        <AppVersion />
      </div>
    </>
  );
}

export default AppLayout;
